import React, { useCallback, useEffect, useMemo } from "react"
import { JSONSchema7, JSONSchema7Definition } from "json-schema"
import { Loader } from "dash-styled"
import { useDispatch } from "react-redux"
import { SingleField } from "../../SingleField/SingleField"
import { MultiField } from "../../MultiField/MultiField"
// import { changeInputValue } from "../../../../../../../Actions/Client/InputRender/InputRenderActions"
import { InputMode } from "../Container/Container"
import {
    JobSubmitData,
    fromInputs,
    getCount,
    getSchema
} from "../../../../../../../Models/Client/JobSubmitData"
import type { UiSchema } from "@rjsf/core"
import {
    downloadAsJSON,
    downloadAsCSV
} from "../../../../../../../Helpers/Functions/downloadAs"
import { jsonconvert } from "../../../../../../../Helpers/Functions/convert"
import { useJobSubmitDataParser } from "../../../../../../../Hooks/useJobSubmitDataParser"
import { JobSubmitDataParseStatus } from "../../../../../../../Models/Client/JobSubmitDataParseStatus"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import {
    findExamples,
    validate
} from "../../../../../../../Helpers/Schema/Schema"
import { uploadData } from "../../../../../../../Actions/API/Data/DataActions"
import { appendTimestampToFileName } from "../../../../../../../Helpers/Functions/random"
import { MainThunkDispatch } from "../../../../../../../Reducers"
import { fromUiSchema } from "../../../../../../../Models/Client/FileType"
import { InputRenderEntry } from "../../../../../../../Reducers/Client/InputRender/InputRenderReducer"

export type InputRenderData = InputRenderEntry & { canSubmit?: boolean }

interface RenderProps {
    orgUsername: string
    appID: string | undefined
    contextName: string
    schema: JSONSchema7
    uiSchema: UiSchema
    id: string
    testId?: string
    defaultData?: JobSubmitData
    activeMode: InputMode
    single?: boolean
    uploaderStateKey: string
    inputs?: InputRenderData
    initiateSubmit: boolean
    stopSubmit: () => void
    onChangeMode: (button: InputMode) => void
    onDirtinessChange?: (isDirty: boolean) => void
    onInputChange: (inputs: InputRenderData) => void
}

export interface VariantRenderProps {
    id: string
    testId?: string
    data?: JobSubmitData
    activeMode: InputMode
    uiSchema: UiSchema
    schema: JSONSchema7
    errors: any[]
    single: boolean
    parseState: JobSubmitDataParseStatus
    freshImport: boolean
    onLoadedImport: () => void
    onChange: (
        data: JobSubmitData,
        errors?: unknown[],
        canSubmit?: boolean
    ) => void
    onChangeMode: (button: InputMode) => void
    onDownloadCSVExample: () => void
    onDownloadJSONExample: () => void
    onDropFile: (received: File[], rejected?: File[]) => void
    onClearFile: () => void
    handleUploadBlob: (blob: Blob, name?: string) => Promise<string>
    initiateSubmit: boolean
    stopSubmit: () => void
}

/**
 * Schema Property tuple for using in determining the complexity of
 * the data structure
 */
export type SchemaProperty = [string, JSONSchema7Definition | undefined]

function isSingleProperty(props: SchemaProperty[]): props is [SchemaProperty] {
    return props.length === 1
}

/**
 * Job Input render variant
 *
 * - [assumption] input schema is an "object"
 * - we render value as an array of items
 * - 1 value, during onboarding, submits as "tracked" onboarding job
 * - 2+ values, or use outside onboarding, is submitted as normal array of jobs
 * - Choices aren't likely to be rendered here
 * - Anything that isn't a pure text or upload-style input, is just JSON input
 * - Single field (ie 1 object property) input gets "full" treatment
 * - Multi field (ie 2+ object property) input gets "compressed" treatment
 *
 * surviveReroute by default
 * onboarding handles submission
 */
export const Render: React.FC<RenderProps> = ({
    orgUsername,
    appID,
    id,
    contextName,
    schema,
    uiSchema,
    defaultData,
    testId,
    onChangeMode,
    activeMode,
    single = false,
    uploaderStateKey,
    onDirtinessChange,
    onInputChange,
    inputs,
    initiateSubmit,
    stopSubmit
}) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    // const formData = useAppState(state => state.client.inputRender.entries[id])
    const d = inputs?.values ?? defaultData
    const objProps = useMemo(
        () =>
            schema.properties === undefined
                ? []
                : Object.entries(schema.properties),
        [schema]
    )

    const updateInput = useCallback(
        (data: JobSubmitData, errors?: unknown[], canSubmit?: boolean) => {
            if (errors !== undefined) {
                return onInputChange({
                    values: data,
                    errors: [],
                    formErrors: errors,
                    canSubmit
                })
            }

            const jobSubmissionSchema = getSchema(
                schema,
                single && activeMode !== "csv" // csv input can have more than 1 data, but in onboarding only 1st line will be taken
            )

            onInputChange({
                values: data,
                errors: validate(jobSubmissionSchema, data).errors || []
            })
        },
        [activeMode, schema, single, onInputChange]
    )

    const dummyData = useMemo(
        () => findExamples(schema, single),
        [schema, single]
    )

    const [parseState, processFile, resetFile] = useJobSubmitDataParser(
        schema,
        single
    )
    const handleDownloadJSONExample = useCallback(
        () => downloadAsJSON(dummyData, `input__json__${contextName}`),
        [contextName, dummyData]
    )
    const [freshImport, setImportToFresh, setImportToStale] = useBoolean(false)

    const handleDownloadCSVExample = useCallback(async () => {
        const dummyCSV = dummyData.map((item, index) => {
            return {
                ...(typeof item === "object" && item !== null ? item : {}),
                "id (optional)": `${index + 1}`,
                "extra data 1": "",
                "extra data 2": "",
                etc: ""
            }
        })
        const csv = await jsonconvert({ json: dummyCSV })

        downloadAsCSV(csv, `input__csv__${contextName}`)
    }, [contextName, dummyData])

    const handleUploadBlob = useCallback(
        (blob: Blob, name?: string) =>
            dispatch(uploadData(blob, appendTimestampToFileName(name ?? ""))),
        [dispatch]
    )
    // reset when activeMode changes
    useEffect(() => {
        resetFile()
        if (activeMode === "json") {
            updateInput(fromInputs(dummyData, single))
        }
    }, [activeMode, dummyData, resetFile, single, updateInput])

    const handleDropFile = useCallback(
        (acceptedFiles: File[], rejectedFiles?: File[]) => {
            setImportToFresh()
            processFile(acceptedFiles, rejectedFiles)
        },
        [processFile, setImportToFresh]
    )

    const handleResetFile = useCallback(() => {
        updateInput(fromInputs(activeMode === "json" ? dummyData : [], single))
        resetFile()
    }, [activeMode, dummyData, resetFile, single, updateInput])

    useEffect(() => {
        if (d === undefined && activeMode === "json") {
            updateInput(fromInputs(dummyData, single))
        }
    }, [activeMode, d, dummyData, updateInput, single])

    useEffect(() => {
        if (parseState.type === "parsed") {
            updateInput(parseState.data)
        }
    }, [onChangeMode, parseState, updateInput])

    useEffect(() => {
        if (
            parseState.type === "inProgress" &&
            ((parseState.fileType === "json" && activeMode === "csv") ||
                (parseState.fileType === "csv" && activeMode === "json"))
        ) {
            onChangeMode(parseState.fileType)
        }
    }, [activeMode, onChangeMode, parseState])

    useEffect(() => {
        if (parseState.type === "inProgress") {
            updateInput(fromInputs([], single))
        }
    }, [parseState.type, single, updateInput])

    useEffect(() => {
        const itemCount =
            d === undefined ? undefined : getCount(d as JobSubmitData)
        if (onDirtinessChange !== undefined) {
            if (
                activeMode === "json" &&
                JSON.stringify(d) !==
                    JSON.stringify(fromInputs(dummyData, single))
            ) {
                onDirtinessChange(true)
            } else if (activeMode !== "json") {
                onDirtinessChange(itemCount !== undefined && itemCount > 0)
            } else {
                onDirtinessChange(false)
            }
        }
    }, [activeMode, d, dummyData, onDirtinessChange, single])

    if (
        d !== undefined &&
        (inputs === undefined || inputs.values === undefined) &&
        defaultData !== undefined
    ) {
        updateInput(defaultData)
        return <Loader />
    }

    /**
     * This variant is for inputSchema only, which is assumed
     * to be an object at it's root level, if not it's unknown.
     */
    if (objProps.length === 0) return null

    const props: VariantRenderProps = {
        id,
        schema,
        testId,
        data: d as JobSubmitData,
        uiSchema,
        onChangeMode,
        activeMode,
        onChange: updateInput,
        errors: inputs ? inputs.formErrors ?? inputs.errors : [],
        single,
        parseState,
        freshImport,
        initiateSubmit,
        stopSubmit,
        onLoadedImport: setImportToStale,
        onDownloadCSVExample: handleDownloadCSVExample,
        onDownloadJSONExample: handleDownloadJSONExample,
        onClearFile: handleResetFile,
        onDropFile: handleDropFile,
        handleUploadBlob
    }
    /**
     * If we have 1 object property, we use "SingleField"
     * If we have 2+ properties, we use "MultiField"
     */
    return isSingleProperty(objProps) ? (
        <SingleField
            orgUsername={orgUsername}
            appID={appID}
            schemaType={
                objProps.length > 0
                    ? fromUiSchema(uiSchema[objProps[0][0]])
                    : undefined
            }
            {...props}
            uploaderStateKey={uploaderStateKey}
            initialJsonValue={dummyData}
        />
    ) : (
        <MultiField
            orgUsername={orgUsername}
            appID={appID}
            initialJsonValue={dummyData}
            {...props}
        />
    )
}
